import { CircularProgress, Typography } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'
import useCompanyConfig from '../../hooks/api/useCompanyConfig'
import useProducts from '../../hooks/api/useProducts'
import useAlert from '../../hooks/useAlert'
import { ProductsForm } from '../../settings/Products'
import { authedAxios } from '../../utils'

import WizardContainer from '../shared/WizardContainer'
import WizardFooter from '../shared/WizardFooter'

const Products = () => {
  const { navigateToNext, wizardState, updateWizardState } = useOutletContext()

  const {
    companyConfig,
    setCompanyConfig,
    updateCompanyConfig,
    ..._companyConfig
  } = useCompanyConfig()

  const { products = [] } = wizardState

  const hasInitializedData = useMemo(() => !!wizardState.products, [])

  const {
    products: productsData,
    setProducts: setProductsData,
    billingCycleTypes,
    saveProducts,
    dirty,
    isValid,

    ..._products
  } = useProducts(companyConfig, true)

  useEffect(() => {
    if (hasInitializedData) return setProductsData(products)

    updateWizardState({ products: productsData })
  }, [productsData])

  const setProducts = (products) => {
    setProductsData(products)
    updateWizardState({ products })
  }

  const isLoading = _companyConfig.isLoading || _products.isLoading

  const { createAlert } = useAlert()

  const submit = () => {
    const newConfig = {
      ...companyConfig,
      hasSavedProducts: true,
    }

    updateCompanyConfig(newConfig)
      .then(saveProducts)
      .then(() => {
        navigateToNext()
        setCompanyConfig(newConfig)
      })
      .catch(() => {
        createAlert({
          severity: 'error',
          message: 'Something went wrong',
          action: {
            label: 'Skip this step',
            onClick: () => navigateToNext(),
          },
        })
      })
  }

  if (isLoading) return <CircularProgress />

  return (
    <WizardContainer>
      <Typography variant={'h5'}>Products</Typography>

      <Typography sx={{ mb: 2 }}>
        Use the fields below to define all of the products and services that you
        offer, what you charge for them, and their billing cycles. And don’t
        forget, these can be updated whenever you want. In our example, we're a
        small cable company:
      </Typography>

      <ProductsForm
        products={products}
        setProducts={setProducts}
        billingCycleTypes={billingCycleTypes}
      />

      <WizardFooter
        primaryButton={
          {
            disabled: !products.every((p) => !!p.name) || !dirty || !isValid,
            label: 'Save and continue',
            onClick: submit,
          } || {}
        }
      />
    </WizardContainer>
  )
}

export default Products
